import React from 'react';
import { Button } from '../../../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../../../components/ui/card';
import { Zap, Flame, CheckCircle, Award } from 'lucide-react';
import { cn } from '@/lib/utils';

const LeaderboardContent = ({ students }) => {
  // Admin kullanıcıları filtrele (gifajans@gmail.com ve role=admin olanlar)
  const filteredStudents = (students || []).filter(s => 
    s.email !== 'gifajans@gmail.com' && 
    s.role !== 'admin' &&
    !s.name?.toLowerCase().includes('fatih şengül')
  );
  
  // Gerçek öğrenci verisinden liderlik tablosu oluştur
  const sortedByMiles = [...filteredStudents].sort((a, b) => (b.miles || 0) - (a.miles || 0)).slice(0, 20);
  const sortedByStreak = [...filteredStudents].sort((a, b) => (b.streak || 0) - (a.streak || 0)).slice(0, 10);
  const sortedByLessons = [...filteredStudents].sort((a, b) => (b.completed_lessons || 0) - (a.completed_lessons || 0)).slice(0, 10);
  
  const getRankBadge = (rank) => {
    if (rank === 1) return { bg: 'bg-amber-400', text: 'text-white', emoji: '🥇' };
    if (rank === 2) return { bg: 'bg-gray-300', text: 'text-gray-700', emoji: '🥈' };
    if (rank === 3) return { bg: 'bg-orange-300', text: 'text-orange-800', emoji: '🥉' };
    return { bg: 'bg-gray-100', text: 'text-gray-600', emoji: '' };
  };

  return (
    <div className="space-y-6" data-testid="leaderboard-content">
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-2xl font-bold text-gray-800">Liderlik Tablosu</h2>
          <p className="text-gray-500">En başarılı öğrenciler ve performans sıralaması</p>
        </div>
        <div className="flex gap-2">
          <Button variant="outline" size="sm">Bu Hafta</Button>
          <Button variant="outline" size="sm">Bu Ay</Button>
          <Button variant="outline" size="sm">Tüm Zamanlar</Button>
        </div>
      </div>

      {/* Top 3 Showcase */}
      <div className="grid md:grid-cols-3 gap-4">
        {sortedByMiles.slice(0, 3).map((student, idx) => {
          const positions = [
            { order: 'order-2', scale: 'scale-110', color: 'from-amber-400 to-amber-600' },
            { order: 'order-1', scale: '', color: 'from-gray-400 to-gray-600' },
            { order: 'order-3', scale: '', color: 'from-orange-400 to-orange-600' },
          ];
          const pos = positions[idx];
          return (
            <Card key={student.id} className={cn("border-0 shadow-lg", pos.order, pos.scale)}>
              <CardContent className="p-6 text-center">
                <div className={cn("w-20 h-20 mx-auto rounded-full flex items-center justify-center text-3xl font-bold text-white bg-gradient-to-br mb-4", pos.color)}>
                  {student.name?.charAt(0) || '?'}
                </div>
                <div className="text-4xl mb-2">{getRankBadge(idx + 1).emoji}</div>
                <h3 className="font-bold text-lg text-gray-800">{student.name}</h3>
                <p className="text-sm text-gray-500 mb-3">{student.email}</p>
                <div className="flex justify-center gap-4">
                  <div className="text-center">
                    <p className="text-2xl font-bold text-blue-600">{(student.miles || 0).toLocaleString()}</p>
                    <p className="text-xs text-gray-500">Mil</p>
                  </div>
                  <div className="text-center">
                    <p className="text-2xl font-bold text-orange-500">{student.streak || 0}</p>
                    <p className="text-xs text-gray-500">Seri</p>
                  </div>
                </div>
              </CardContent>
            </Card>
          );
        })}
      </div>

      {/* Full Leaderboard */}
      <div className="grid lg:grid-cols-3 gap-6">
        {/* Mil Sıralaması */}
        <Card className="border-0 shadow-lg">
          <CardHeader className="pb-2">
            <CardTitle className="flex items-center gap-2 text-lg">
              <Zap className="w-5 h-5 text-blue-500" />
              Mil Sıralaması
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-2">
              {sortedByMiles.map((student, idx) => {
                const badge = getRankBadge(idx + 1);
                return (
                  <div key={student.id} className="flex items-center gap-3 p-2 rounded-lg hover:bg-gray-50 transition-colors">
                    <div className={cn("w-8 h-8 rounded-full flex items-center justify-center font-bold text-sm", badge.bg, badge.text)}>
                      {idx + 1}
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className="font-medium text-gray-800 text-sm truncate">{student.name}</p>
                      <p className="text-xs text-gray-500">{(student.miles || 0).toLocaleString()} Mil</p>
                    </div>
                    {badge.emoji && <span className="text-lg">{badge.emoji}</span>}
                  </div>
                );
              })}
            </div>
          </CardContent>
        </Card>

        {/* Seri Sıralaması */}
        <Card className="border-0 shadow-lg">
          <CardHeader className="pb-2">
            <CardTitle className="flex items-center gap-2 text-lg">
              <Flame className="w-5 h-5 text-orange-500" />
              En Uzun Seriler
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-2">
              {sortedByStreak.map((student, idx) => {
                const badge = getRankBadge(idx + 1);
                return (
                  <div key={student.id} className="flex items-center gap-3 p-2 rounded-lg hover:bg-gray-50 transition-colors">
                    <div className={cn("w-8 h-8 rounded-full flex items-center justify-center font-bold text-sm", badge.bg, badge.text)}>
                      {idx + 1}
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className="font-medium text-gray-800 text-sm truncate">{student.name}</p>
                      <p className="text-xs text-gray-500">{student.streak || 0} gün 🔥</p>
                    </div>
                    {badge.emoji && <span className="text-lg">{badge.emoji}</span>}
                  </div>
                );
              })}
            </div>
          </CardContent>
        </Card>

        {/* Ders Tamamlama Sıralaması */}
        <Card className="border-0 shadow-lg">
          <CardHeader className="pb-2">
            <CardTitle className="flex items-center gap-2 text-lg">
              <CheckCircle className="w-5 h-5 text-green-500" />
              En Çok Ders Tamamlayan
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-2">
              {sortedByLessons.map((student, idx) => {
                const badge = getRankBadge(idx + 1);
                return (
                  <div key={student.id} className="flex items-center gap-3 p-2 rounded-lg hover:bg-gray-50 transition-colors">
                    <div className={cn("w-8 h-8 rounded-full flex items-center justify-center font-bold text-sm", badge.bg, badge.text)}>
                      {idx + 1}
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className="font-medium text-gray-800 text-sm truncate">{student.name}</p>
                      <p className="text-xs text-gray-500">{student.completed_lessons || 0} ders</p>
                    </div>
                    {badge.emoji && <span className="text-lg">{badge.emoji}</span>}
                  </div>
                );
              })}
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Seviye Bazında Liderler */}
      <Card className="border-0 shadow-lg">
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Award className="w-5 h-5 text-purple-500" />
            Seviye Bazında En İyiler
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="grid md:grid-cols-6 gap-4">
            {['A0', 'A1', 'A2', 'B1', 'B2', 'C1'].map(level => {
              const levelStudents = filteredStudents.filter(s => s.current_level === level);
              const topStudent = levelStudents.sort((a, b) => (b.miles || 0) - (a.miles || 0))[0];
              return (
                <div key={level} className="bg-gray-50 rounded-xl p-4 text-center">
                  <div className="text-2xl font-bold text-blue-600 mb-2">{level}</div>
                  {topStudent ? (
                    <>
                      <div className="w-12 h-12 mx-auto bg-gradient-to-br from-blue-400 to-purple-500 rounded-full flex items-center justify-center text-white font-bold mb-2">
                        {topStudent.name?.charAt(0)}
                      </div>
                      <p className="font-medium text-sm text-gray-800 truncate">{topStudent.name}</p>
                      <p className="text-xs text-gray-500">{(topStudent.miles || 0).toLocaleString()} Mil</p>
                    </>
                  ) : (
                    <p className="text-sm text-gray-400">Henüz öğrenci yok</p>
                  )}
                </div>
              );
            })}
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

export default LeaderboardContent;
